import styled from 'styled-components';

import { tokens, media } from '../../../design/tokens';

/**
 * Moldura do herói.
 *
 * O banner é ativo comercial do cliente e a arte fica intacta — o que muda é
 * o enquadramento em volta dela.
 *
 * Antes o wrapper trazia `linear-gradient(360deg, rgb(15,15,15) 22%,
 * rgb(226,226,226) 77%)`: um CINZA CLARO atrás do slider, que viola a "Regra
 * do Palco" do DESIGN.md e aparecia em conexão lenta, antes de o banner
 * pintar. Removido — é cor fora do sistema, então tirá-la respeita o
 * congelamento da paleta em vez de contrariá-lo.
 */
export const SliderMaskWrap = styled.div`
  position: relative;
  width: 100%;
  background: ${tokens.color.stageBlack};

  /*
   * A moldura acompanha a LARGURA, não a altura da janela.
   *
   * Com min-height de 55vh a proporção da moldura dependia do tamanho da
   * janela do aluno, e o cover cortava por onde desse. Medido neste tenant:
   * arte de 1440x440 (3.27:1) numa moldura de 2.17:1 — o cover enchia a
   * altura e comia 34% da largura, decepando o título que a escola mandou
   * desenhar. 16/5 (3.2:1) é a proporção de banner web mais comum e fica
   * perto o suficiente para a arte ocupar a moldura inteira.
   */
  div.keen-slider {
    aspect-ratio: 16 / 5;
    max-height: 560px;

    ${media.upTo(tokens.breakpoint.md)} {
      aspect-ratio: 2 / 1;
      max-height: 320px;
    }

    /* Fallback para o piso de navegador do projeto, onde aspect-ratio não
       existe: altura derivada da largura pelo mesmo 3.2:1. */
    @supports not (aspect-ratio: 16 / 5) {
      height: 31.25vw;
      max-height: 560px;

      ${media.upTo(tokens.breakpoint.md)} {
        height: 50vw;
      }
    }
  }

  /* O banner se dissolve na primeira fileira em vez de terminar num corte
     reto. É o idioma da "vinheta interna" que o DESIGN.md já pratica, agora
     na junção entre herói e catálogo. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 42%;
    z-index: 1;
    pointer-events: none;

    background: linear-gradient(
      to top,
      ${tokens.color.stageBlack} 0%,
      rgba(15, 15, 15, 0.72) 24%,
      rgba(15, 15, 15, 0) 100%
    );
  }
`;

export const SlideLink = styled.a`
  display: block;
  width: 100%;
  height: 100%;
`;

export const SlideImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;

  /*
   * contain, não cover. O banner é ativo comercial do cliente, com título
   * e logo embutidos na arte, e as proporções variam entre os 24 tenants —
   * qualquer cover acaba decepando o texto de alguém. Com contain nada é
   * cortado, nunca, em nenhum cliente. A sobra vira faixa preta que some no
   * palco, porque o fundo já é #0f0f0f: letterbox invisível.
   */
  object-fit: contain;
  object-position: center center;
`;

/**
 * Setas do herói.
 *
 * Eram `<svg onClick>` com estilo inline: não focáveis, sem nome acessível e
 * inoperáveis por teclado. Agora são botões de verdade.
 */
export const SliderArrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${(props) => (props.$side === 'left' ? 'left: 0;' : 'right: 0;')}
  width: clamp(44px, 5%, 96px);
  z-index: 2;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;
  cursor: pointer;
  color: ${tokens.color.textWhite};
  background: transparent;

  opacity: 0;
  transition: opacity ${tokens.motion.quick} ease;

  > svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
    filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.8));
  }

  ${SliderMaskWrap}:hover &,
  ${SliderMaskWrap}:focus-within &,
  &:focus-visible {
    opacity: 1;
  }

  @media (hover: none) {
    opacity: 1;
  }
`;

/** Indicadores de posição — o herói não dizia em qual banner você está. */
export const SliderDots = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${tokens.space.md};
  z-index: 2;

  display: flex;
  justify-content: center;
  align-items: center;
`;

export const SliderDot = styled.button<{ $active: boolean }>`
  /* Largura fixa e scaleX no lugar de animar width: largura é layout, e
     animá-la força reflow a cada troca de banner. A caixa de toque continua
     com os 20px cheios mesmo quando o indicador aparece pequeno. */
  width: 20px;
  height: 6px;
  margin: 0 ${tokens.space.xs};
  padding: 0;

  border: 0;
  border-radius: ${tokens.radius.md};
  cursor: pointer;
  background: transparent;

  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    border-radius: ${tokens.radius.md};

    transform: scaleX(${(props) => (props.$active ? 1 : 0.3)});
    background: ${(props) =>
      props.$active ? tokens.color.marqueeGold : 'rgba(255, 255, 255, 0.45)'};
    transition: transform ${tokens.motion.quick} ease,
      background ${tokens.motion.quick} ease;
  }
`;
